import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  FiActivity as RawActivityIcon,
  FiArrowDown as RawArrowDownIcon,
  FiArrowUp as RawArrowUpIcon,
  FiAward as RawAwardIcon,
  FiBarChart2 as RawChartIcon,
  FiBox as RawBoxIcon,
  FiDatabase as RawDatabaseIcon,
  FiPlus as RawPlusIcon,
  FiRefreshCw as RawRefreshIcon,
  FiSend as RawSendIcon,
} from 'react-icons/fi';
import { connector as connectorApi, leaderboard as leaderboardApi } from '../../services/api';
import type {
  Leaderboard,
  LeaderboardAlgorithm,
  LeaderboardRow,
  LeaderboardRun,
  MetaDataset,
  RunStatus,
  Submission,
} from '../../types/leaderboard';
import {
  deltaToneClass,
  formatDuration,
  formatScore,
  formatRunSpread,
  formatUsageCoefficient,
  formatUsageCost,
  formatUsageTokens,
  groupLeaderboardsByVersion,
  isReferenceAlgorithm,
  isSubmissionActive,
  newIdempotencyKey,
  parseLeaderboardVersion,
  runStatusMeta,
  sampleCoverage,
  submissionStatusMeta,
  totalErrors,
  accessErrorText,
  authorLabel,
  isOwnedBy,
  type SortableColumn,
  sortLeaderboardRows,
  rankByAlgorithmId,
  versionLabel,
} from '../../utils/leaderboardSpec';
import { useAuthContext } from '../../contexts/AuthContext';
import LeaderboardSubmissionsModal from './LeaderboardSubmissionsModal';
import MetaDatasetForm from './MetaDatasetForm';
import './Leaderboard.css';

/**
 * Вкладка Leaderboard в админ-панели (AIT-115): список лидербордов слева,
 * таблица выбранного справа.
 *
 * Порядок строк приходит с бэкенда уже с учётом направления метрики. Здесь он не
 * трогается: сортировка по убыванию перевернула бы таблицу при `minimize`.
 */

const errorText = (e: unknown, fallback: string): string => {
  const err = e as {
    message?: string;
    response?: {
      data?: {
        error?: string | { message?: string; details?: unknown };
        message?: string;
        details?: unknown;
      };
    };
  };
  const data = err.response?.data;
  const apiError = typeof data?.error === 'string' ? data.error : data?.error?.message;
  const head = apiError || data?.message || err.message || fallback;
  // Отказ запуска объясняет не сообщение, а details: там разбор бенчмарка по
  // полям, и причин бывает сразу несколько. Без него на экране оставалось
  // «ml service rejected the run», а настоящая причина — только в логах пода.
  const details = typeof data?.error === 'string' ? data?.details : data?.error?.details ?? data?.details;
  if (details === undefined || details === null || details === '') return head;
  return `${head}: ${typeof details === 'string' ? details : JSON.stringify(details)}`;
};

const StatusPill: React.FC<{ status?: string }> = ({ status }) => {
  if (!status) return <span className="lb-muted">—</span>;
  const meta = submissionStatusMeta(status);
  return (
    <span className={`lb-pill lb-pill--${meta.tone}`} title={meta.hint}>
      {meta.label}
    </span>
  );
};

const RunStatusPill: React.FC<{ status: string }> = ({ status }) => {
  const meta = runStatusMeta(status);
  return <span className={`lb-pill lb-pill--${meta.tone}`} title={meta.hint}>{meta.label}</span>;
};

const RUN_TERMINAL: RunStatus[] = ['succeeded', 'partially_succeeded', 'failed', 'timeout', 'cancelled'];

/**
 * Состояние прогона обновляется слиянием, а не заменой (AIP-2062).
 *
 * Ответ `POST …/runs/{id}/cancel` приходил без `id`, и замена состояния этим
 * телом обнуляла идентификатор: следующий опрос уходил на
 * `GET /auto-tune/leaderboards/runs/undefined`, бэкенд отвечал
 * `400 invalid run_id`, статус больше не обновлялся и прогон навсегда оставался
 * в «Cancelling». Идентификатор берём из прошлого состояния, когда в ответе его
 * нет; по той же причине не теряем список сабмишенов, если ответ его не несёт.
 */
function mergeRun(
  prev: LeaderboardRun | null,
  incoming: LeaderboardRun | null | undefined
): LeaderboardRun | null {
  if (!incoming) return prev;
  const id = incoming.id || prev?.id;
  // Прогон без идентификатора опрашивать и отменять нечем — прежнее состояние
  // полезнее, чем объект, на который нельзя сослаться.
  if (!id) return prev;
  return { ...prev, ...incoming, id };
}
// react-icons in this project is typed against a newer ReactNode definition.
const FiActivity = RawActivityIcon as React.ComponentType<{ className?: string }>;
const FiArrowDown = RawArrowDownIcon as React.ComponentType<{ className?: string }>;
const FiArrowUp = RawArrowUpIcon as React.ComponentType<{ className?: string }>;
const FiAward = RawAwardIcon as React.ComponentType<{ className?: string }>;
const FiBarChart2 = RawChartIcon as React.ComponentType<{ className?: string }>;
const FiBox = RawBoxIcon as React.ComponentType<{ className?: string }>;
const FiDatabase = RawDatabaseIcon as React.ComponentType<{ className?: string }>;
const FiPlus = RawPlusIcon as React.ComponentType<{ className?: string }>;
const FiRefreshCw = RawRefreshIcon as React.ComponentType<{ className?: string }>;
const FiSend = RawSendIcon as React.ComponentType<{ className?: string }>;

type SortState = { column: SortableColumn; dir: 'asc' | 'desc' } | null;

/**
 * Вынесен из тела `LeaderboardPanel` намеренно: объявление компонента внутри
 * функции компонента создавало НОВЫЙ тип на каждый рендер, и React
 * размонтировал/монтировал `th`/`button` заново — клавиатурный фокус улетал на
 * `body` после каждого клика по заголовку, и то же самое происходило на
 * каждом автообновлении таблицы (раз в 15 секунд, пока есть активный сабмишен).
 */
const SortableHeader: React.FC<{ column: SortableColumn; sort: SortState; onToggle: (column: SortableColumn) => void; children: React.ReactNode }> = ({
  column,
  sort,
  onToggle,
  children,
}) => {
  const active = sort?.column === column;
  return (
    <th
      className={`lb-th--sortable ${active ? 'lb-th--sorted' : ''}`}
      aria-sort={active ? (sort!.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <button type="button" onClick={() => onToggle(column)}>
        {children}
        {active ? (sort!.dir === 'asc' ? <FiArrowUp /> : <FiArrowDown />) : null}
      </button>
    </th>
  );
};

const LeaderboardPanel: React.FC = () => {
  const [view, setView] = useState<'ranking' | 'algorithms' | 'datasets'>('ranking');
  const [boards, setBoards] = useState<Leaderboard[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [rows, setRows] = useState<LeaderboardRow[]>([]);
  const [loadingBoards, setLoadingBoards] = useState(true);
  const [loadingTable, setLoadingTable] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [historyRow, setHistoryRow] = useState<LeaderboardRow | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [algorithms, setAlgorithms] = useState<LeaderboardAlgorithm[]>([]);
  const [loadingAlgorithms, setLoadingAlgorithms] = useState(true);
  const [algorithmQuery, setAlgorithmQuery] = useState('');
  const [selectedAlgorithms, setSelectedAlgorithms] = useState<Set<string>>(new Set());
  const [showDisabled, setShowDisabled] = useState(true);
  const [run, setRun] = useState<LeaderboardRun | null>(null);
  const [creatingRun, setCreatingRun] = useState(false);
  // AIT-246: табло общее. Свой прогон отличается от чужого только по user_id.
  const { user } = useAuthContext();
  const currentUserId = (user as { id?: string } | null | undefined)?.id;
  const [onlyMine, setOnlyMine] = useState(false);
  const [sort, setSort] = useState<SortState>(null);
  /**
   * Прогон, запущенный в этой сессии, — свой, даже если бэкенд не вернул
   * `user_id`. Иначе пользователь терял отмену собственного прогона.
   */
  const startedHere = useRef<Set<string>>(new Set());

  const [cancellingRun, setCancellingRun] = useState(false);
  const [cancellingSubmission, setCancellingSubmission] = useState<string | null>(null);
  const [metaDatasets, setMetaDatasets] = useState<MetaDataset[]>([]);
  /**
   * `dataset_id` → человеческое имя. В сабмишене приходит только `case_id`
   * вида `current:<uuid>`, и в детализации по датасетам стоял голый uuid —
   * по нему не понять, какой датасет просел. Имена лежат в каталоге
   * интеграции, поэтому карта строится один раз на выбранное табло, а не
   * запрашивается в модалке на каждое открытие.
   */
  const [datasetNames, setDatasetNames] = useState<Record<string, string>>({});
  const [loadingMetaDatasets, setLoadingMetaDatasets] = useState(true);
  const [editingMetaDataset, setEditingMetaDataset] = useState<MetaDataset | null>(null);
  const runIntentRef = useRef<{ signature: string; key: string } | null>(null);
  // Слаги версий (`foo`, `foo_v1`, `foo_v2`) сворачиваются в одну группу в
  // списке мета-датасетов — раскрыта только по явному клику по шеврону. Список
  // табло версии больше не разворачивает (AIT-318): версия там выбирается в
  // шапке таблицы, а не в боковом списке.
  const [expandedMetaGroups, setExpandedMetaGroups] = useState<Set<string>>(new Set());

  /**
   * `pickSelected` решает, что делать с выбором после рефетча. По умолчанию —
   * сохранить прежний выбор, если он ещё есть в списке, иначе взять первый
   * (обычный рефетч). `selectMetaDatasetBoard` подставляет свой пикер, у
   * которого промах — не повод молча показать чужое табло.
   */
  const loadBoards = useCallback(async (pickSelected?: (items: Leaderboard[]) => string | null) => {
    setLoadingBoards(true);
    setError(null);
    try {
      const res = await leaderboardApi.list({ limit: 50 });
      const items = res.data?.items ?? [];
      setBoards(items);
      setSelectedId((prev) => {
        if (pickSelected) return pickSelected(items) ?? prev;
        return prev && items.some((item) => item.id === prev) ? prev : items[0]?.id ?? null;
      });
    } catch (e) {
      setError(errorText(e, 'Could not load leaderboards.'));
    } finally {
      setLoadingBoards(false);
    }
  }, []);

  const loadTable = useCallback(async (id: string) => {
    setLoadingTable(true);
    try {
      const res = await leaderboardApi.getTable(id);
      setRows(res.data?.rows ?? []);
    } catch (e) {
      setError(errorText(e, 'Could not load the leaderboard table.'));
    } finally {
      setLoadingTable(false);
    }
  }, []);

  const loadAlgorithms = useCallback(async () => {
    setLoadingAlgorithms(true);
    try {
      const res = await leaderboardApi.listAlgorithms({ include_disabled: true, limit: 500 });
      const items = res.data?.items ?? [];
      setAlgorithms(items);
      const enabledIds = new Set(items.filter((item) => item.enabled).map((item) => item.algorithm_id));
      setSelectedAlgorithms((current) => new Set(Array.from(current).filter((id) => enabledIds.has(id))));
    } catch (e) {
      setError(errorText(e, 'Could not load the algorithm catalogue.'));
    } finally {
      setLoadingAlgorithms(false);
    }
  }, []);

  const loadMetaDatasets = useCallback(async () => {
    setLoadingMetaDatasets(true);
    try {
      const res = await leaderboardApi.listMetaDatasets({ limit: 100 });
      setMetaDatasets(res.data?.items ?? []);
    } catch (e) {
      setError(errorText(e, 'Could not load meta-datasets.'));
    } finally {
      setLoadingMetaDatasets(false);
    }
  }, []);

  /**
   * Мета-датасет мог форкнуться при сохранении (другой `id`/`slug`) — после
   * такого сейва выбранным должно стать ЕГО табло. Промах — табло не попало в
   * первую страницу списка, либо табло только что созданного мета-датасета
   * ещё не появилось на момент рефетча — не повод молча показать чужое:
   * прежний выбор остаётся как есть.
   */
  const selectMetaDatasetBoard = useCallback(
    (metaDataset: MetaDataset) =>
      loadBoards(
        (items) =>
          items.find((b) => b.meta_dataset_id === metaDataset.id || b.meta_dataset_slug === metaDataset.slug)?.id ?? null
      ),
    [loadBoards]
  );

  useEffect(() => {
    void loadBoards();
    void loadAlgorithms();
    void loadMetaDatasets();
  }, [loadBoards, loadAlgorithms, loadMetaDatasets]);

  useEffect(() => {
    if (selectedId) void loadTable(selectedId);
    // Другое табло — другие значения колонок; своя сортировка сбрасывается
    // назад к порядку бэкенда, а не тащится на чужие данные.
    setSort(null);
  }, [selectedId, loadTable]);

  const toggleSort = (column: SortableColumn) => {
    setSort((prev) => {
      if (!prev || prev.column !== column) return { column, dir: 'desc' };
      if (prev.dir === 'desc') return { column, dir: 'asc' };
      return null;
    });
  };

  // Пока хоть одна строка считается — обновляем таблицу сами.
  const hasActive = useMemo(
    () => rows.some((r) => r.best_submission && isSubmissionActive(r.best_submission.status)),
    [rows]
  );
  useEffect(() => {
    if (!hasActive || !selectedId) return undefined;
    const t = window.setInterval(() => void loadTable(selectedId), 15000);
    return () => window.clearInterval(t);
  }, [hasActive, selectedId, loadTable]);

  const runId = run?.id;
  useEffect(() => {
    // Без идентификатора опрос не запускается вовсе: иначе адрес собирался бы с
    // `undefined` и каждые пять секунд получал 400 (AIP-2062).
    if (!runId || !run || RUN_TERMINAL.includes(run.status)) return undefined;
    const t = window.setInterval(async () => {
      try {
        const res = await leaderboardApi.getRun(runId);
        const next = res.data;
        if (!next) return;
        setRun((prev) => mergeRun(prev, next));
        if (RUN_TERMINAL.includes(next.status) && selectedId) void loadTable(selectedId);
      } catch (e) {
        setError(errorText(e, 'Could not refresh the run.'));
      }
    }, 5000);
    return () => window.clearInterval(t);
  }, [runId, run, selectedId, loadTable]);

  // Имена датасетов выбранного табло. Каталог принадлежит интеграции, поэтому
  // без неё имён не получить — у мета-датасета, собранного не из интеграции,
  // так и останутся идентификаторы, и это честнее выдуманной подписи.
  useEffect(() => {
    const board = boards.find((b) => b.id === selectedId);
    const meta = metaDatasets.find((m) => m.id === board?.meta_dataset_id);
    const integrationId = meta?.integration_id;
    if (!integrationId) {
      setDatasetNames({});
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await connectorApi.getDatasets(integrationId);
        if (cancelled) return;
        const names: Record<string, string> = {};
        for (const item of res.data ?? []) {
          const label = typeof item.label === 'string' ? item.label.trim() : '';
          if (!label) continue;
          if (typeof item.id === 'string' && item.id.trim()) names[item.id.trim()] = label;
        }
        setDatasetNames(names);
      } catch {
        // Каталог недоступен — показываем идентификаторы, как раньше. Отдельная
        // ошибка на экране тут только мешала бы: таблица уже отрисована.
        if (!cancelled) setDatasetNames({});
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedId, boards, metaDatasets]);

  const selected = boards.find((b) => b.id === selectedId) ?? null;
  const direction = selected?.metric_direction === 'minimize' ? 'minimize' : 'maximize';
  const enabledAlgorithms = algorithms.filter((item) => item.enabled).length;
  const completedRows = rows.filter((row) => row.best_submission?.status === 'succeeded').length;
  // Счёт своих строк: под ним же прячется тумблер — без своих он не нужен.
  const mineCount = rows.filter((row) => isOwnedBy(row.best_submission, currentUserId)).length;
  /**
   * Сегодня `run` попадает сюда только из собственного запуска и опроса того же
   * прогона, то есть чужой прогон на экран не приходит. Проверка оставлена как
   * защита на случай, когда на доске появится список прогонов табло.
   */
  const ownsRun = !!run && (isOwnedBy(run, currentUserId) || startedHere.current.has(run.id));
  // Порядок строк приходит с бэкенда и не пересортировывается сам по себе;
  // фильтр только убирает строки, а колоночная сортировка — явный выбор поверх него.
  const filteredRows = onlyMine ? rows.filter((row) => isOwnedBy(row.best_submission, currentUserId)) : rows;
  const visibleRows = useMemo(() => sortLeaderboardRows(filteredRows, sort), [filteredRows, sort]);
  // Ранг и медаль остаются в порядке бекенда даже когда своя сортировка колонки
  // переставила строки на экране — иначе клик по «Coefficient» ставит на первое
  // место с золотой плашкой самый расточительный алгоритм, пока подиум выше
  // по-прежнему считает первым лидера по бекендовому порядку.
  const rankById = useMemo(() => rankByAlgorithmId(filteredRows), [filteredRows]);
  // Группа определяется только слагом (`foo`, `foo_v1`, `foo_v2`) — боковой
  // список показывает по одной карточке на базу, версия выбирается в шапке.
  const boardGroups = useMemo(() => groupLeaderboardsByVersion(boards), [boards]);
  // Группа, которой принадлежит текущий выбор — какая бы версия ни была
  // выбрана. Нужна и переключателю версий в шапке, и выпадашке в Algorithms.
  const selectedGroup = useMemo(
    () => boardGroups.find((group) => group.versions.some((v) => v.id === selectedId)) ?? null,
    [boardGroups, selectedId]
  );
  // Мета-датасеты версионируются тем же слагом, но носят его в поле `slug`, а
  // не `meta_dataset_slug` — геттер слага параметризован именно под это.
  const metaDatasetGroups = useMemo(
    () => groupLeaderboardsByVersion(metaDatasets, (m) => m.slug),
    [metaDatasets]
  );
  const toggleMetaGroup = (baseSlug: string) => {
    setExpandedMetaGroups((prev) => {
      const next = new Set(prev);
      next.has(baseSlug) ? next.delete(baseSlug) : next.add(baseSlug);
      return next;
    });
  };
  const podiumRows = rows
    .filter((row) => typeof row.best_submission?.primary_score === 'number')
    .slice(0, 3);
  const normalizedAlgorithmQuery = algorithmQuery.trim().toLowerCase();
  const filteredAlgorithms = useMemo(
    () => algorithms.filter((item) => {
      if (!normalizedAlgorithmQuery) return true;
      return [item.name, item.algorithm_id, item.source, item.kind, item.reason]
        .some((value) => String(value ?? '').toLowerCase().includes(normalizedAlgorithmQuery));
    }),
    [algorithms, normalizedAlgorithmQuery]
  );
  const visibleEnabledAlgorithms = filteredAlgorithms.filter((item) => item.enabled);
  const visibleDisabledAlgorithms = filteredAlgorithms.filter((item) => !item.enabled);
  const runSubmissions = run?.submissions ?? [];
  const runFinished = runSubmissions.filter((submission) => !isSubmissionActive(submission.status)).length;
  const runProgress = runSubmissions.length ? Math.round((runFinished / runSubmissions.length) * 100) : 0;

  const syncAlgorithms = async () => {
    setSyncing(true);
    setNotice(null);
    try {
      await leaderboardApi.syncAlgorithms();
      setNotice('Algorithm catalogue pulled from ML.');
      await loadAlgorithms();
      if (selectedId) await loadTable(selectedId);
    } catch (e) {
      setError(errorText(e, 'Could not sync the algorithm catalogue.'));
    } finally {
      setSyncing(false);
    }
  };

  const toggleAlgorithm = (algorithmId: string) => {
    setSelectedAlgorithms((prev) => {
      const next = new Set(prev);
      next.has(algorithmId) ? next.delete(algorithmId) : next.add(algorithmId);
      return next;
    });
  };

  const createRun = async () => {
    if (!selectedId || selectedAlgorithms.size === 0) return;
    const enabledIds = new Set(algorithms.filter((item) => item.enabled).map((item) => item.algorithm_id));
    const algorithmIds = Array.from(selectedAlgorithms).filter((id) => enabledIds.has(id)).sort();
    if (algorithmIds.length === 0) {
      setSelectedAlgorithms(new Set());
      setError('The selected algorithms are no longer available. Refresh the catalogue and choose again.');
      return;
    }
    const signature = `${selectedId}:${algorithmIds.join('|')}`;
    if (!runIntentRef.current || runIntentRef.current.signature !== signature) {
      runIntentRef.current = { signature, key: newIdempotencyKey() };
    }
    setCreatingRun(true);
    setError(null);
    try {
      const res = await leaderboardApi.createRun(selectedId, {
        algorithm_ids: algorithmIds,
        idempotency_key: runIntentRef.current.key,
      });
      if (res.data?.id) startedHere.current.add(res.data.id);
      // Прогон без `id` невозможно ни опросить, ни отменить — это отказ, а не успех.
      if (!res.success || !res.data?.id) throw new Error(res.error?.message || 'The run was not accepted.');
      // Именно замена, а не слияние: это другой прогон, поля прошлого сюда тянуть нельзя.
      setRun(mergeRun(null, res.data));
      setSelectedAlgorithms(new Set());
      runIntentRef.current = null;
      setNotice(`Run started for ${algorithmIds.length} algorithm(s).`);
      await loadTable(selectedId);
    } catch (e) {
      setError(errorText(e, 'Could not start the run.'));
    } finally {
      setCreatingRun(false);
    }
  };

  const cancelRun = async () => {
    if (!run) return;
    setCancellingRun(true);
    try {
      const res = await leaderboardApi.cancelRun(run.id);
      setRun((prev) => mergeRun(prev, res.data));
      setNotice('Cancellation requested. A submission already running may finish normally.');
    } catch (e) {
      const status = (e as { response?: { status?: number } })?.response?.status;
      setError(accessErrorText(status, '') || errorText(e, 'Could not cancel the run.'));
    } finally {
      setCancellingRun(false);
    }
  };

  const cancelSubmission = async (submission: Submission) => {
    setCancellingSubmission(submission.id);
    try {
      const res = await leaderboardApi.cancelSubmission(submission.id);
      // Строка сабмишена тоже обновляется слиянием: ответ без `id` иначе стёр бы
      // ключ строки и кнопку отмены на ней (та же причина, что и у прогона).
      setRun((prev) => prev ? {
        ...prev,
        submissions: prev.submissions?.map((item) =>
          item.id === submission.id ? { ...item, ...(res.data ?? {}), id: item.id } : item
        ),
      } : prev);
      setNotice('Submission cancellation requested. A running job may not stop immediately.');
    } catch (e) {
      const status = (e as { response?: { status?: number } })?.response?.status;
      setError(accessErrorText(status, '') || errorText(e, 'Could not cancel the submission.'));
    } finally {
      setCancellingSubmission(null);
    }
  };

  const deleteMetaDataset = async (item: MetaDataset) => {
    if (!window.confirm(`Delete “${item.name || item.slug}” and its leaderboard history?`)) return;
    try {
      await leaderboardApi.deleteMetaDataset(item.id);
      setNotice('Meta-dataset and its leaderboard were deleted.');
      await Promise.all([loadMetaDatasets(), loadBoards()]);
    } catch (e) {
      setError(errorText(e, 'Could not delete the meta-dataset.'));
    }
  };

  const selectVisibleAlgorithms = () => {
    setSelectedAlgorithms((current) => {
      const next = new Set(current);
      visibleEnabledAlgorithms.forEach((item) => next.add(item.algorithm_id));
      return next;
    });
  };

  const submissionAlgorithmLabel = (submission: Submission, index: number): { name: string; id: string } => {
    const details = submission as Submission & {
      algorithm_id?: string;
      algorithm?: { id?: string; algorithm_id?: string; name?: string };
    };
    const reference = details.algorithm?.algorithm_id || details.algorithm_id || details.algorithm?.id || '';
    const match = algorithms.find((item) => item.algorithm_id === reference || item.id === reference);
    return {
      name: details.algorithm?.name || match?.name || `Submission ${index + 1}`,
      id: match?.algorithm_id || reference || submission.id,
    };
  };

  const renderAlgorithmCards = (items: LeaderboardAlgorithm[]) => (
    <div className="lb-algorithms">
      {items.map((item) => (
        <label key={item.id} className={`lb-algorithm-card ${item.enabled ? '' : 'lb-algorithm-card--off'}`}>
          <input type="checkbox" disabled={!item.enabled} checked={selectedAlgorithms.has(item.algorithm_id)} onChange={() => toggleAlgorithm(item.algorithm_id)} />
          <span className="lb-alg">
            <span className="lb-alg__name">{item.name}</span>
            <code className="lb-alg__id">{item.algorithm_id}</code>
            <span className="lb-alg__tags">
              <span className="lb-tag">{item.source || 'unknown'}</span>
              {item.kind ? <span className="lb-tag">{item.kind}</span> : null}
              {isReferenceAlgorithm(item.algorithm_id) ? (
                <span className="lb-tag lb-tag--reference" title="Reference algorithm: only rephrases the prompt, a noise floor rather than a contender">reference</span>
              ) : null}
              {!item.enabled ? <span className="lb-tag lb-tag--off">disabled</span> : null}
            </span>
            {!item.enabled && item.reason ? <span className="lb-alg__reason">{item.reason}</span> : null}
          </span>
        </label>
      ))}
    </div>
  );

  /**
   * Одна карточка табло в боковом списке — теперь всегда голова группы версий
   * (AIT-318): версия внутри группы выбирается переключателем в шапке
   * таблицы, а не здесь, поэтому карточка сама не носит метку версии — она
   * называла бы только одну из уже выбранных версий и вводила бы в заблуждение.
   * `active` передаётся отдельно: подсветка должна оставаться, даже если
   * реально выбрана не сама голова, а другая версия той же группы.
   */
  const renderBoardCard = (b: Leaderboard, active: boolean) => (
    <button
      key={b.id}
      type="button"
      className={`lb-boardcard ${active ? 'lb-boardcard--active' : ''}`}
      onClick={() => setSelectedId(b.id)}
    >
      <span className="lb-boardcard__top">
        <span className="lb-boardcard__name">{b.name || b.meta_dataset_slug || b.id}</span>
        <span className="lb-boardcard__dot" aria-hidden="true" />
      </span>
      <span className="lb-boardcard__meta">
        {b.meta_dataset_slug ? <code>{b.meta_dataset_slug}</code> : null}
        <code>{b.primary_metric_path || 'validation.score'}</code>
        <span>{b.metric_direction === 'minimize' ? <FiArrowDown /> : <FiArrowUp />} {b.metric_direction === 'minimize' ? 'Lower wins' : 'Higher wins'}</span>
      </span>
    </button>
  );

  /**
   * Одна строка мета-датасета — вынесена из `metaDatasets.map`, чтобы рисовать
   * и голову группы версий, и развёрнутые версии под ней с теми же кнопками
   * Edit/Delete на каждой (правка и удаление — это операция над конкретной
   * версией, а не над группой).
   */
  const renderMetaDatasetItem = (item: MetaDataset, version: number) => {
    const hasSplitDetails = Array.isArray(item.train) || Array.isArray(item.validation) || Array.isArray(item.test);
    return (
      <div className="lb-meta-item" key={item.id}>
        <div>
          <strong>{item.name || item.slug}</strong>
          <span className="lb-tag lb-tag--version">{versionLabel(version)}</span>
          <code>{item.slug}</code>
          <span className="lb-muted">
            {hasSplitDetails
              ? `${item.train?.length ?? 0} train · ${item.validation?.length ?? 0} validation · ${item.test?.length ?? 0} test`
              : 'Open to view dataset splits'}
          </span>
        </div>
        <div className="lb__toolbar-actions">
          <button
            type="button"
            className="lb-btn lb-btn--xs"
            onClick={async () => {
              try {
                const res = await leaderboardApi.getMetaDataset(item.id);
                setEditingMetaDataset(res.data ?? item);
              } catch (e) {
                setError(errorText(e, 'Could not load the meta-dataset.'));
              }
            }}
          >
            Edit
          </button>
          <button type="button" className="lb-btn lb-btn--xs lb-btn--danger" onClick={() => void deleteMetaDataset(item)}>
            Delete
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="lb" data-testid="lb-panel">
      <header className="lb-hero">
        <div className="lb-hero__top">
          <div className="lb-hero__identity">
            <span className="lb-hero__mark" aria-hidden="true"><FiAward /></span>
            <div className="lb-hero__copy">
              <span className="lb-eyebrow">Benchmark control center</span>
              <h2 className="lb__title">Algorithm leaderboards</h2>
              <p>Find the strongest prompt-tuning strategy across reproducible evaluation suites.</p>
            </div>
          </div>
          <div className="lb-hero__actions">
            <span className="lb-live-status"><span aria-hidden="true" /> Live benchmark data</span>
            <div className="lb__toolbar-actions">
              <button type="button" className="lb-btn lb-btn--sync" onClick={() => void syncAlgorithms()} disabled={syncing}>
                <FiRefreshCw className={syncing ? 'lb-spin' : ''} /> {syncing ? 'Syncing…' : 'Sync catalogue'}
              </button>
              <button type="button" className="lb-btn lb-btn--primary" onClick={() => setFormOpen(true)}>
                <FiPlus /> New meta-dataset
              </button>
            </div>
          </div>
        </div>
        <div className="lb-stats" aria-label="Leaderboard summary">
          <div className="lb-stat"><span className="lb-stat__icon lb-stat__icon--blue"><FiBarChart2 /></span><span><small>Leaderboards</small><strong>{boardGroups.length}</strong><em>Available boards</em></span></div>
          <div className="lb-stat"><span className="lb-stat__icon lb-stat__icon--orange"><FiBox /></span><span><small>Active algorithms</small><strong>{enabledAlgorithms}</strong><em>Ready to compete</em></span></div>
          <div className="lb-stat"><span className="lb-stat__icon lb-stat__icon--ink"><FiActivity /></span><span><small>Scored entries</small><strong>{completedRows}</strong><em>On this board</em></span></div>
          <div className="lb-stat"><span className="lb-stat__icon lb-stat__icon--soft"><FiDatabase /></span><span><small>Meta-datasets</small><strong>{metaDatasetGroups.length}</strong><em>Benchmark suites</em></span></div>
        </div>
      </header>

      <nav className="lb-nav" aria-label="Leaderboard sections">
        <button type="button" className={view === 'ranking' ? 'lb-nav__item lb-nav__item--active' : 'lb-nav__item'} onClick={() => setView('ranking')}><FiBarChart2 /> Ranking</button>
        <button type="button" className={view === 'algorithms' ? 'lb-nav__item lb-nav__item--active' : 'lb-nav__item'} onClick={() => setView('algorithms')}><FiBox /> Algorithms <span>{algorithms.length}</span></button>
        <button type="button" className={view === 'datasets' ? 'lb-nav__item lb-nav__item--active' : 'lb-nav__item'} onClick={() => setView('datasets')}><FiDatabase /> Meta-datasets <span>{metaDatasetGroups.length}</span></button>
      </nav>

      {error ? (
        <div className="lb-banner lb-banner--danger">
          {error}
          <button type="button" className="lb-banner__close" onClick={() => setError(null)} aria-label="Dismiss">
            ×
          </button>
        </div>
      ) : null}
      {notice ? (
        <div className="lb-banner lb-banner--ok">
          {notice}
          <button type="button" className="lb-banner__close" onClick={() => setNotice(null)} aria-label="Dismiss">
            ×
          </button>
        </div>
      ) : null}

      {view === 'ranking' ? <div className="lb__body">
        <aside className="lb__list" aria-label="Leaderboards">
          <div className="lb__list-head"><span>Boards <strong>{boardGroups.length}</strong></span><button type="button" onClick={() => void loadBoards()} title="Refresh leaderboards" aria-label="Refresh leaderboards"><FiRefreshCw /></button></div>
          {loadingBoards && boards.length === 0 ? (
            <p className="lb-muted">Loading…</p>
          ) : boards.length === 0 ? (
            <div className="lb-list-empty">
              <p className="lb-muted">No leaderboards yet.</p>
              <button type="button" className="lb-btn lb-btn--xs" onClick={() => setFormOpen(true)}>
                Create a meta-dataset
              </button>
            </div>
          ) : (
            // Одна карточка на группу — версия выбирается в шапке таблицы, не
            // здесь. Активна, когда выбрана ЛЮБАЯ версия группы, иначе выбор
            // версии не был бы виден в этом списке вовсе.
            boardGroups.map((group) => renderBoardCard(group.head, group.versions.some((v) => v.id === selectedId)))
          )}
        </aside>

        <section className="lb__table-wrap">
          {!selected ? (
            <p className="lb-muted">Pick a leaderboard.</p>
          ) : (
            <>
              <div className="lb__table-head">
                <div>
                  <span className="lb-eyebrow">Live ranking</span>
                  <h3>{selected.name || selected.meta_dataset_slug || 'Leaderboard'}</h3>
                  <span className="lb-table__summary">
                    {rows.length} contenders · {completedRows} scored
                    {mineCount > 0 ? ` · ${mineCount} yours` : null}
                  </span>
                  {/* Одна версия в группе — переключателю нечего переключать,
                      разметка шапки не меняется вовсе. */}
                  {selectedGroup && selectedGroup.versions.length > 1 ? (
                    <div className="lb-version-row">
                      <div className="lb-version-switch" role="tablist" aria-label="Leaderboard version">
                        {selectedGroup.versions.map((v) => {
                          const version = parseLeaderboardVersion(v.meta_dataset_slug || v.id).version;
                          const active = v.id === selectedId;
                          return (
                            <button
                              key={v.id}
                              type="button"
                              role="tab"
                              aria-selected={active}
                              className={`lb-nav__item ${active ? 'lb-nav__item--active' : ''}`}
                              onClick={() => setSelectedId(v.id)}
                            >
                              {versionLabel(version)}
                            </button>
                          );
                        })}
                      </div>
                      {/* Слаг называет версию — имя таблоида его не отличает,
                          копируется с оригинала при форке. */}
                      <code className="lb-alg__id">{selected.meta_dataset_slug}</code>
                    </div>
                  ) : null}
                </div>
                {mineCount > 0 ? (
                  <label className="lb-onlymine">
                    <input type="checkbox" checked={onlyMine} onChange={(e) => setOnlyMine(e.target.checked)} />
                    <span>Only mine</span>
                  </label>
                ) : null}
                <span className="lb-metric">
                  <span>Primary metric</span>
                  <code>{selected.primary_metric_path || 'validation.score'}</code>
                  <strong>{direction === 'minimize' ? <FiArrowDown /> : <FiArrowUp />} {direction === 'minimize' ? 'Lower is better' : 'Higher is better'}</strong>
                </span>
              </div>

              {podiumRows.length ? (
                <div className="lb-podium-wrap">
                  {/* Подпись вынесена из ленты: раньше она занимала место призёра и
                      говорила «Top 3» рядом с двумя карточками. */}
                  <div className="lb-podium__label">
                    <span className="lb-eyebrow">Front runners</span>
                    <small>
                      {podiumRows.length} of {rows.length} scored · best submission of each
                    </small>
                  </div>
                  <div className="lb-podium" aria-label="Top ranked algorithms">
                    {podiumRows.map((row, index) => {
                      const score = row.best_submission?.primary_score;
                      const lead = podiumRows[0]?.best_submission?.primary_score;
                      // Отставание от лидера: считается одинаково в обе стороны,
                      // потому что лидер уже определён порядком с бэкенда.
                      const gap =
                        index > 0 && typeof score === 'number' && typeof lead === 'number'
                          ? Math.abs(score - lead)
                          : null;
                      return (
                        <button
                          key={row.algorithm.id}
                          type="button"
                          className={`lb-podium__place lb-podium__place--${index + 1}`}
                          onClick={() => setHistoryRow(row)}
                          title="Open submission history"
                        >
                          <span className="lb-podium__rank">{index + 1}</span>
                          <span className="lb-podium__name">{row.algorithm.name}</span>
                          <strong>{formatScore(score)}</strong>
                          <small>
                            {row.submissions_count} {row.submissions_count === 1 ? 'run' : 'runs'}
                            {gap != null ? (
                              <span className="lb-podium__gap">{formatScore(gap)} behind</span>
                            ) : (
                              <span className="lb-podium__gap lb-podium__gap--lead">leader</span>
                            )}
                          </small>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ) : null}

              {loadingTable && rows.length === 0 ? (
                <p className="lb-muted">Loading table…</p>
              ) : rows.length === 0 ? (
                // Пустая таблица — нормальное состояние нового лидерборда, не ошибка.
                <div className="lb-empty">
                  <p>No submissions scored yet.</p>
                  <p className="lb-muted">Submit algorithms to this leaderboard and the table will fill in.</p>
                </div>
              ) : (
                <div className="lb-table__scroll">
                  <table className="lb-table" data-testid="lb-table">
                    <thead>
                      <tr>
                        <th className="lb-table__rank">#</th>
                        <th>Algorithm</th>
                        <th>Runs</th>
                        <th>Status</th>
                        <th>Primary</th>
                        <SortableHeader column="train" sort={sort} onToggle={toggleSort}>Train</SortableHeader>
                        <SortableHeader column="validation" sort={sort} onToggle={toggleSort}>Validation</SortableHeader>
                        <SortableHeader column="test" sort={sort} onToggle={toggleSort}>
                          <span title="Test score of the run picked by validation; the line under it is the average over all succeeded runs of this algorithm">Test</span>
                        </SortableHeader>
                        <th>Test Δ</th>
                        <th>Coverage</th>
                        <SortableHeader column="usage" sort={sort} onToggle={toggleSort}>Usage</SortableHeader>
                        <SortableHeader column="coefficient" sort={sort} onToggle={toggleSort}>
                          <span title="Usage divided by the tokens of the datasets — how much the algorithm spent per unit of data">Coef.</span>
                        </SortableHeader>
                        <SortableHeader column="algoCoefficient" sort={sort} onToggle={toggleSort}>
                          <span title="Algorithm's own spend only, without the actor or the judge — comparable to hackathon coefficients, unlike the overall one">Algo coef.</span>
                        </SortableHeader>
                      </tr>
                    </thead>
                    <tbody>
                      {visibleRows.map((row) => {
                        const sub = row.best_submission;
                        const coverage = sampleCoverage(sub?.sample_stats);
                        const errs = totalErrors(sub?.error_stats);
                        const runSpread = formatRunSpread(row.test_score_mean, row.test_score_stddev, row.succeeded_count);
                        // Ранг из порядка бекенда, не из позиции в текущей (возможно
                        // пересортированной кликом по колонке) отображаемой строке.
                        const rank = rankById.get(row.algorithm.id) ?? 0;
                        return (
                          <tr
                            key={row.algorithm.id}
                            // Приёмка AIT-116: попап открывается по клику на строку.
                            className={`lb-table__row ${row.algorithm.enabled ? '' : 'lb-table__row--off'} ${
                              row.submissions_count > 0 ? 'lb-table__row--clickable' : ''
                            }`}
                            onClick={row.submissions_count > 0 ? () => setHistoryRow(row) : undefined}
                            title={row.submissions_count > 0 ? 'Open submission history' : undefined}
                          >
                            <td className="lb-table__rank"><span className={`lb-rank lb-rank--${Math.min(rank, 4)}`}>{rank}</span></td>
                            <td>
                              <div className="lb-alg">
                                <span className="lb-alg__name">{row.algorithm.name}</span>
                                <code className="lb-alg__id">{row.algorithm.algorithm_id}</code>
                                <span className="lb-alg__tags">
                                  {row.algorithm.source ? <span className="lb-tag">{row.algorithm.source}</span> : null}
                                  {isReferenceAlgorithm(row.algorithm.algorithm_id) ? (
                                    <span className="lb-tag lb-tag--reference" title="Reference algorithm: only rephrases the prompt, a noise floor rather than a contender">reference</span>
                                  ) : null}
                                  {!row.algorithm.enabled ? <span className="lb-tag lb-tag--off">disabled</span> : null}
                                  {authorLabel(row.best_submission, currentUserId) ? (
                                    <span
                                      className={`lb-tag lb-tag--author ${
                                        isOwnedBy(row.best_submission, currentUserId) ? 'lb-tag--mine' : ''
                                      }`}
                                      title={row.best_submission?.user_id}
                                    >
                                      {authorLabel(row.best_submission, currentUserId)}
                                    </span>
                                  ) : null}
                                </span>
                                {!row.algorithm.enabled && row.algorithm.reason ? (
                                  <span className="lb-alg__reason">{row.algorithm.reason}</span>
                                ) : null}
                              </div>
                            </td>
                            <td>
                              <button
                                type="button"
                                className="lb-btn lb-btn--xs"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setHistoryRow(row);
                                }}
                                disabled={row.submissions_count === 0}
                                title={`${row.submissions_count} attempt${row.submissions_count === 1 ? '' : 's'} total, including failed and queued`}
                              >
                                {typeof row.succeeded_count === 'number' ? row.succeeded_count : row.submissions_count}
                                {typeof row.succeeded_count === 'number' ? (
                                  <span className="lb-muted"> / {row.submissions_count}</span>
                                ) : null}
                              </button>
                            </td>
                            <td>
                              <StatusPill status={sub?.status} />
                            </td>
                            <td className="lb-num lb-num--strong">{formatScore(sub?.primary_score)}</td>
                            <td className="lb-num">{formatScore(sub?.train_score)}</td>
                            <td className="lb-num">{formatScore(sub?.validation_score)}</td>
                            <td className="lb-num">
                              {/* Значение — выбранного по валидации прогона, как и
                                  Test Δ рядом. Среднее по серии стоит подписью, а не
                                  на месте значения: иначе в одной строке два
                                  источника, и дельта считается от другого числа. */}
                              <div className="lb-usage">
                                <span>{formatScore(sub?.test_final_score)}</span>
                                {runSpread ? <span className="lb-usage__dur">{runSpread}</span> : null}
                              </div>
                            </td>
                            <td className={`lb-num ${deltaToneClass(sub?.test_delta, direction)}`}>
                              {formatScore(sub?.test_delta)}
                            </td>
                            <td>
                              {coverage ? (
                                <span className={coverage.thin ? 'lb-cov lb-cov--thin' : 'lb-cov'}>
                                  {coverage.scored}/{coverage.total}
                                  {errs > 0 ? <span className="lb-errs-inline"> · {errs} err</span> : null}
                                </span>
                              ) : (
                                <span className="lb-muted">—</span>
                              )}
                            </td>
                            <td className="lb-num">
                              <div className="lb-usage">
                                <span>{formatUsageTokens(sub?.usage_totals, sub?.usage_status)}</span>
                                <span className="lb-usage__dur">
                                  {formatUsageCost(sub?.usage_totals, sub?.usage_status)} · {formatDuration(sub?.duration_s)}
                                </span>
                              </div>
                            </td>
                            <td className="lb-num">{formatUsageCoefficient(sub?.usage_coefficient, sub?.usage_status)}</td>
                            <td className="lb-num">{formatUsageCoefficient(sub?.usage_coefficient_algorithm, sub?.usage_status)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </section>
      </div> : null}

      {view === 'algorithms' ? <section className="lb-section lb-section--catalogue" data-testid="lb-algorithms">
        <header className="lb-section__head">
          <div>
            <span className="lb-eyebrow">Algorithm registry</span>
            <h3>Choose contenders</h3>
            <span className="lb-muted">Select one or more enabled algorithms and send them to the active leaderboard.</span>
          </div>
          <div className="lb__toolbar-actions">
            <label className="lb-field lb-run-target">
              <span className="lb-field__label">Leaderboard</span>
              <select
                className="lb-input"
                value={selectedGroup?.baseSlug ?? ''}
                disabled={creatingRun}
                onChange={(event) => {
                  const group = boardGroups.find((g) => g.baseSlug === event.target.value);
                  setSelectedId(group ? group.head.id : null);
                }}
              >
                <option value="">Select a leaderboard…</option>
                {boardGroups.map((group) => (
                  <option key={group.baseSlug} value={group.baseSlug}>
                    {group.head.name || group.head.meta_dataset_slug || group.head.id}
                  </option>
                ))}
              </select>
            </label>
            {/* Версия — отдельный селект, скрыт при одной версии: он не должен
                мешать, когда выбирать нечего. Нативный <option> не рисует
                бейджи, поэтому версия — часть текста опции. */}
            {selectedGroup && selectedGroup.versions.length > 1 ? (
              <label className="lb-field lb-run-target">
                <span className="lb-field__label">Version</span>
                <select
                  className="lb-input"
                  value={selectedId ?? ''}
                  disabled={creatingRun}
                  onChange={(event) => setSelectedId(event.target.value || null)}
                >
                  {selectedGroup.versions.map((v) => (
                    <option key={v.id} value={v.id}>
                      {versionLabel(parseLeaderboardVersion(v.meta_dataset_slug || v.id).version)}
                    </option>
                  ))}
                </select>
              </label>
            ) : null}
            <button type="button" className="lb-btn lb-btn--primary" disabled={!selectedId || selectedAlgorithms.size === 0 || creatingRun} onClick={() => void createRun()}>
              <FiSend /> {creatingRun ? 'Submitting…' : `Run selected${selectedAlgorithms.size ? ` (${selectedAlgorithms.size})` : ''}`}
            </button>
          </div>
        </header>
        {loadingAlgorithms ? <p className="lb-muted">Loading algorithms…</p> : (
          <div className="lb-catalogue">
            <div className="lb-catalogue__tools">
              <label className="lb-catalogue__search">
                <i className="fas fa-search" aria-hidden="true" />
                <input className="lb-input" value={algorithmQuery} onChange={(event) => setAlgorithmQuery(event.target.value)} placeholder="Search by name, ID, source or kind…" />
                {algorithmQuery ? <button type="button" onClick={() => setAlgorithmQuery('')} aria-label="Clear algorithm search">×</button> : null}
              </label>
              <span className="lb-muted">{visibleEnabledAlgorithms.length} available</span>
              <button type="button" className="lb-btn lb-btn--xs" disabled={visibleEnabledAlgorithms.length === 0} onClick={selectVisibleAlgorithms}>Select visible</button>
              <button type="button" className="lb-btn lb-btn--xs" disabled={selectedAlgorithms.size === 0} onClick={() => setSelectedAlgorithms(new Set())}>Clear selection</button>
            </div>

            <div className="lb-catalogue__group">
              <div className="lb-catalogue__group-head"><span><i className="fas fa-circle-check" aria-hidden="true" /> Available algorithms</span><strong>{visibleEnabledAlgorithms.length}</strong></div>
              {visibleEnabledAlgorithms.length ? renderAlgorithmCards(visibleEnabledAlgorithms) : <div className="lb-empty"><p>No available algorithms match this search.</p></div>}
            </div>

            <div className="lb-catalogue__disabled-head">
              <button type="button" onClick={() => setShowDisabled((current) => !current)} aria-expanded={showDisabled}>
                <span><i className="fas fa-ban" aria-hidden="true" /> Disabled algorithms <strong>{visibleDisabledAlgorithms.length}</strong></span>
                <span>{showDisabled ? 'Hide' : 'Show'} <i className={`fas fa-chevron-${showDisabled ? 'up' : 'down'}`} aria-hidden="true" /></span>
              </button>
            </div>
            {showDisabled ? (
              <div className="lb-catalogue__group lb-catalogue__group--disabled">
                {visibleDisabledAlgorithms.length ? renderAlgorithmCards(visibleDisabledAlgorithms) : <div className="lb-empty"><p>No disabled algorithms match this search.</p></div>}
              </div>
            ) : null}
          </div>
        )}
      </section> : null}

      {run ? (
        <section className="lb-section" data-testid="lb-run">
          <header className="lb-section__head">
            <div><h3>Current run</h3><code className="lb-alg__id">{run.id}</code></div>
            <div className="lb__toolbar-actions"><RunStatusPill status={run.status} />{!RUN_TERMINAL.includes(run.status) && ownsRun ? <button type="button" className="lb-btn lb-btn--danger" disabled={cancellingRun} onClick={() => void cancelRun()}>{cancellingRun ? 'Cancelling…' : 'Cancel queued work'}</button> : null}
              {!RUN_TERMINAL.includes(run.status) && !ownsRun ? <span className="lb-owner-note">started by {authorLabel(run, currentUserId) ?? 'another user'}</span> : null}</div>
          </header>
          {runSubmissions.length ? <div className="lb-run-progress" aria-label={`${runProgress}% complete`}><div className="lb-run-progress__copy"><span><strong>{runFinished}</strong> of {runSubmissions.length} submissions finished</span><strong>{runProgress}%</strong></div><div className="lb-run-progress__track"><span style={{ width: `${runProgress}%` }} /></div></div> : null}
          <p className="lb-muted">Cancellation marks queued submissions; an already running submission may continue.</p>
          {runSubmissions.length ? <div className="lb-run-items">{runSubmissions.map((sub, index) => { const label = submissionAlgorithmLabel(sub, index); return <div className="lb-run-item" key={sub.id}><span className="lb-run-item__identity"><strong>{label.name}</strong><code>{label.id}</code></span><StatusPill status={sub.status} />{isSubmissionActive(sub.status) ? <button type="button" className="lb-btn lb-btn--xs lb-btn--danger" disabled={cancellingSubmission === sub.id} onClick={() => void cancelSubmission(sub)}>Cancel</button> : null}</div>; })}</div> : <div className="lb-empty lb-empty--compact"><p>Waiting for submissions to be queued…</p></div>}
        </section>
      ) : null}

      {view === 'datasets' ? <section className="lb-section" data-testid="lb-meta-datasets">
        <header className="lb-section__head"><div><span className="lb-eyebrow">Benchmark library</span><h3>Meta-datasets</h3><span className="lb-muted">Manage reproducible evaluation definitions and their splits.</span></div><div className="lb__toolbar-actions"><button type="button" className="lb-btn" onClick={() => void loadMetaDatasets()} disabled={loadingMetaDatasets}><FiRefreshCw className={loadingMetaDatasets ? 'lb-spin' : ''} /> Refresh</button><button type="button" className="lb-btn lb-btn--primary" onClick={() => setFormOpen(true)}><FiPlus /> New meta-dataset</button></div></header>
        {loadingMetaDatasets && metaDatasets.length === 0 ? <p className="lb-muted">Loading meta-datasets…</p> : metaDatasets.length === 0 ? <div className="lb-empty"><p>No meta-datasets yet.</p><p className="lb-muted">Create one to define reproducible train, validation and test splits.</p></div> : <div className="lb-meta-list">{metaDatasetGroups.map((group) => {
          const extraVersions = group.versions.filter((v) => v.id !== group.head.id);
          const expanded = expandedMetaGroups.has(group.baseSlug);
          return (
            <div className="lb-boardgroup" key={group.baseSlug}>
              {renderMetaDatasetItem(group.head, parseLeaderboardVersion(group.head.slug).version)}
              {extraVersions.length ? (
                <button
                  type="button"
                  className="lb-boardgroup__toggle"
                  onClick={() => toggleMetaGroup(group.baseSlug)}
                  aria-expanded={expanded}
                >
                  <span>{group.versions.length} versions</span>
                  <span>{expanded ? 'Hide' : 'Show'} <i className={`fas fa-chevron-${expanded ? 'up' : 'down'}`} aria-hidden="true" /></span>
                </button>
              ) : null}
              {expanded ? extraVersions.map((v) => renderMetaDatasetItem(v, parseLeaderboardVersion(v.slug).version)) : null}
            </div>
          );
        })}</div>}
      </section> : null}

      {historyRow && selectedId ? (
        <LeaderboardSubmissionsModal
          leaderboardId={selectedId}
          row={historyRow}
          datasetNames={datasetNames}
          direction={direction}
          onClose={() => setHistoryRow(null)}
        />
      ) : null}

      {formOpen ? (
        <MetaDatasetForm
          onClose={() => setFormOpen(false)}
          onCreated={(saved) => {
            setNotice('Meta-dataset created together with its leaderboard.');
            void Promise.all([loadMetaDatasets(), saved ? selectMetaDatasetBoard(saved) : loadBoards()]);
          }}
        />
      ) : null}

      {editingMetaDataset ? (
        <MetaDatasetForm
          initialData={editingMetaDataset}
          onClose={() => setEditingMetaDataset(null)}
          onCreated={(saved) => {
            // Правка могла форкнуть запись (другие id/slug) — тогда это уже
            // новая версия, и предупредить надо явно, до того как список
            // молча переключится на неё.
            setNotice(
              saved?.forked
                ? `Editing created a new version — switched to "${saved.slug}".`
                : 'Meta-dataset updated.'
            );
            void Promise.all([loadMetaDatasets(), saved ? selectMetaDatasetBoard(saved) : loadBoards()]);
          }}
        />
      ) : null}
    </div>
  );
};

export default LeaderboardPanel;
